
import React from 'react';
import { Picture } from 'ui/components';
import styles from './ImageOverlay.module.scss';

export type OverlayItemType = 'Tekst' | 'Knap' | 'Dymo';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface ImageOverlayItemProps {
    id: string | number;
    type: OverlayItemType;
    html: string;
    linkUrl?: string;
    linkTarget?: string;
    textColor?: string;
    backgroundColor?: string;
    backgroundOpacity?: number;
    buttonFontSize?: string;
    buttonHeadingLevel?: string;
    row: number;
    col: number;
}

export interface ImageOverlayProps {
    imageUrl: string;
    altText?: string;
    height?: string;
    overlayColor?: string;
    overlayOpacity?: number;
    items: ImageOverlayItemProps[];
}

const GRID_ROWS = 5;
const GRID_COLS = 5;

const getHorizontalTransform = (col: number) => {
    if (col === 0) return '0%';
    if (col === GRID_COLS - 1) return '-100%';
    return '-50%';
};

const getVerticalTransform = (row: number) => {
    if (row === 0) return '0%';
    if (row === GRID_ROWS - 1) return '-100%';
    return '-50%';
};

const getDymoBackgroundColor = (
    hex?: string,
    opacity = 100
): string | undefined => {
    if (!hex) return undefined;

    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
        return undefined;
    }

    const color = parseInt(hex.slice(1), 16);

    const red = (color >> 16) & 255;
    const green = (color >> 8) & 255;
    const blue = color & 255;

    const alpha = Math.min(
        100,
        Math.max(0, opacity)
    ) / 100;

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const renderButton = (item: ImageOverlayItemProps) => {
    const buttonStyle = {
        '--button-color-override': item.textColor,
        '--button-bg-override': item.backgroundColor,
        '--button-border-override': item.backgroundColor,
        fontSize: item.buttonFontSize,
    } as React.CSSProperties;

    // Use a link instead of nesting a button inside an anchor.
    const buttonContent = item.linkUrl ? (
        <a
            href={item.linkUrl}
            target={item.linkTarget || undefined}
            rel={
                item.linkTarget === '_blank'
                    ? 'noopener noreferrer'
                    : undefined
            }
            className={styles.buttonLink}
            style={buttonStyle}
        >
            {item.html}
        </a>
    ) : (
        <span
            className={styles.buttonLink}
            style={buttonStyle}
        >
            {item.html}
        </span>
    );

    const headingLevel = item.buttonHeadingLevel
        ?.trim()
        .match(/^(?:h|overskrift\s*)([1-6])$/i)?.[1];

    if (headingLevel) {
        const Heading = `h${headingLevel}` as HeadingTag;

        return (
            <Heading className={styles.buttonHeading}>
                {buttonContent}
            </Heading>
        );
    }

    return buttonContent;
};

const renderItemContent = (item: ImageOverlayItemProps) => {
    if (item.type === 'Knap') {
        return renderButton(item);
    }

    return (
        <div
            className={
                item.type === 'Dymo'
                    ? styles.dymoContent
                    : styles.textContent
            }
            style={{
                color: item.textColor,
                backgroundColor:
                    item.type === 'Dymo'
                        ? getDymoBackgroundColor(
                            item.backgroundColor,
                            item.backgroundOpacity
                        )
                        : undefined,
            }}
            dangerouslySetInnerHTML={{ __html: item.html }}
        />
    );
};

export const ImageOverlay: React.FC<ImageOverlayProps> = ({
    imageUrl,
    altText,
    height,
    overlayColor,
    overlayOpacity,
    items,
}) => {
    if (!imageUrl) return null;

    const rows = Array.from(
        { length: GRID_ROWS },
        (_, rowIndex) =>
            items
                .filter((item) => item.row === rowIndex)
                .sort((a, b) => a.col - b.col)
    );

    return (
        <div
            className={styles.wrapper}
            style={{
                height: height || 'auto',
                minHeight: height ? 0 : undefined,
            }}
        >
            <Picture
                url={imageUrl}
                altText={altText}
                className={styles.image}
            />

            {overlayColor && (
                <div
                    className={styles.colorOverlay}
                    style={{
                        backgroundColor: overlayColor,
                        opacity:
                            overlayOpacity != null
                                ? Math.min(
                                    100,
                                    Math.max(0, overlayOpacity)
                                ) / 100
                                : 0.3,
                    }}
                />
            )}

            {/* Desktop/tablet: positioned 5x5 grid */}
            <div className={styles.desktopItems}>
                {items.map((item) => (
                    <div
                        key={item.id}
                        className={styles.itemPosition}
                        style={{
                            top: `${((item.row + 0.5) / GRID_ROWS) * 100
                                }%`,
                            left: `${((item.col + 0.5) / GRID_COLS) * 100
                                }%`,
                            transform: `translate(
                                ${getHorizontalTransform(item.col)},
                                ${getVerticalTransform(item.row)}
                            )`,
                        }}
                    >
                        {renderItemContent(item)}
                    </div>
                ))}
            </div>

            {/* Mobile: row-based layout */}
            <div
                className={styles.mobileOverlay}
                style={{
                    gridTemplateRows: `repeat(
                        ${GRID_ROWS},
                        minmax(0, 1fr)
                    )`,
                }}
            >
                {rows.map((rowItems, rowIndex) =>
                    rowItems.length > 0 ? (
                        <div
                            key={rowIndex}
                            className={styles.mobileRow}
                            style={{ gridRow: rowIndex + 1 }}
                        >
                            {rowItems.map((item) => (
                                <div
                                    key={item.id}
                                    className={styles.mobileRowItem}
                                >
                                    {renderItemContent(item)}
                                </div>
                            ))}
                        </div>
                    ) : null
                )}
            </div>
        </div>
    );
};
